// ── Inbox — what "Delete conversation" tells the coach before it deletes ──
//
// Pure words, no DOM and no state, so the wording can be tested on its own and
// so the coach app's copy of it can be held to the same lines.
//
// ⚠️ THIS WARNING MUST MATCH WHAT THE SERVER DOES, and the server does a lot
// (netlify/functions/conversation-action.js, action 'delete'). It is permanent,
// there is no soft delete and no recovery:
//   • ONE-TO-ONE: every `messages` row between this coach and this client, on
//     every channel (in-app, SMS, email, voice notes, call records). The
//     client's app reads the same in-app rows, so THEIR in-app chat is erased
//     too, voice notes included. Texts and emails are different: they already
//     landed on the client's phone and in their inbox, and FitOps cannot reach
//     those, so for them the delete only erases the coach's FitOps records.
//     Saying "erased for the client" about a text would be untrue.
//   • ALSO ONE-TO-ONE: every `scheduled_emails` row for this client, not just
//     the ones the coach scheduled by hand. Today that is three kinds: messages
//     the coach scheduled (scheduled-email-create.js), automatic texts from
//     automations and notifications held overnight until 8am
//     (_shared/quiet-hours.js), and the exit interview follow-up emails queued
//     after a client cancels by email (inbound-email-handler.js). Booked-call
//     reminders are queued with an email or phone and no client_id, so the
//     delete does not reach them, and they are not named. The test beside this
//     file lists every place that queues a waiting message, so a new kind fails
//     it until these words are checked.
//   • GROUP: every message in the group, every membership row and the group
//     itself. `messages` and `scheduled_emails` both cascade from the group row.
// The old warning said only "Delete all messages with X? This cannot be undone."
// and a coach could not tell the client's side goes too, or that scheduled sends
// are cancelled. If the server ever erases more or less, change these lines in
// the same commit; the coach app's copy lives in
// apps/mobile/lib/delete-conversation-warning.ts and a test keeps the two equal.
//
// Plain words, short sentences. The last line is always "cannot be undone" and
// the caller puts it on a line of its own, so it is not buried.

/**
 * @param {{ type?: string, clientName?: string | null, groupName?: string | null }} conv
 *   The open conversation: a group row carries `type: 'group'` and `groupName`,
 *   a one-to-one row carries `clientName`.
 * @returns {string[]} plain-text lines, no markup. The caller escapes them
 *   before putting them in the dialog (the names are typed by people).
 */
export function deleteConversationWarningLines(conv) {
  if (conv && conv.type === 'group') {
    const group = String(conv.groupName || '').trim();
    return [
      group ? `Delete the group ${group}?` : 'Delete this group?',
      'This erases every message in the group, for you and for every member. The group is removed, so members can no longer open it.',
      'Any messages scheduled for the group are cancelled.',
      'This cannot be undone.',
    ];
  }
  const name = String((conv && conv.clientName) || '').trim();
  const who = name || 'this client';
  const Who = name || 'This client';
  return [
    name ? `Delete the whole conversation with ${name}?` : 'Delete this whole conversation?',
    // Only the in-app chat is erased on the client's side.
    `This erases the in-app chat, voice notes included, for you and for ${who}. Their in-app chat will be empty too.`,
    // Texts and emails already reached the client; only the coach's records go.
    `It also erases every text, email and call record with ${who} from your FitOps records. ${Who} keeps the texts and emails already on their phone and in their inbox.`,
    // Every waiting send for the client, the automatic ones too (see the header).
    `Messages still waiting to send to ${who} are cancelled too. These are messages you scheduled, automatic texts held until morning, and exit interview follow-up emails.`,
    'This cannot be undone.',
  ];
}
